import { useCustom, useCustomMutation, useTranslate } from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  List,
  Result,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { graphqlErrorInfo } from '../../../utils/graphqlErrors';

/**
 * E50 · Backoffice — Erase a person (board cc-backoffice ch02: A1 ErasureInventory, B1 Confirm &
 * erase). The operator looks the person up and reads EXACTLY what erasing them would do before
 * anything is touched; then re-types the person's own key (never a generic "yes") and gives the
 * audit reason. The same `planErasure` / `erasePerson` the CLI runs; a blocker disables the erase.
 */
type Count = { name: string; count: number };
type PersonInfo = {
  subject: string;
  key: string;
  uid: string | null;
  email: string | null;
  role: number | null;
  practiceId: string | null;
  stripeCustomer: boolean;
};
type LogCounts = {
  eventLog: number;
  usageLog: number;
  commandLog: number;
  kept: number;
  valueHits: number;
};
type Inventory = {
  person: PersonInfo;
  mergedIn: string[];
  deletes: Count[];
  cohort: boolean;
  pilot: boolean;
  redactions: Count[];
  keptRows: Count[];
  log: LogCounts;
  external: {
    bucket: string;
    account: boolean;
    accessDoc: boolean;
    onLists: number;
    files: Count[];
    urlFiles: number;
  };
  targets: Array<{ name: string; policy: string; detail: string | null }>;
  byHand: string[];
  affectedPractices: string[];
  blockers: string[];
};
type Step = {
  store: string;
  outcome: string;
  counts: string;
  note: string | null;
  until: string | null;
};
type ErasureResult = {
  subject: string;
  alreadyErased: boolean;
  steps: Step[];
  log: LogCounts;
  byHand: string[];
  affectedPractices: string[];
};

const PERSON = [
  'subject',
  'key',
  'uid',
  'email',
  'role',
  'practiceId',
  'stripeCustomer',
];
const COUNT = ['name', 'count'];
const LOG = ['eventLog', 'usageLog', 'commandLog', 'kept', 'valueHits'];
const STEP = ['store', 'outcome', 'counts', 'note', 'until'];

type LookupKind = 'email' | 'subject' | 'uid';

const POLICY_COLOR: Record<string, string> = {
  erase: 'red',
  keep: 'blue',
  expires: 'gold',
  manual: 'purple',
  none: 'default',
};

const countsTable = (rows: Count[], empty: string, testId: string) =>
  rows.length ? (
    <Table<Count>
      size="small"
      rowKey="name"
      pagination={false}
      dataSource={rows}
      data-testid={testId}
      columns={[
        { dataIndex: 'name', render: (v: string) => <code>{v}</code> },
        { dataIndex: 'count', align: 'right' },
      ]}
      showHeader={false}
    />
  ) : (
    <Typography.Text type="secondary">{empty}</Typography.Text>
  );

export const BackofficeErase = () => {
  const translate = useTranslate();
  const { token } = theme.useToken();
  const [params] = useSearchParams();
  const [kind, setKind] = useState<LookupKind>('email');
  const [value, setValue] = useState('');
  const [lookup, setLookup] = useState<{
    kind: LookupKind;
    value: string;
  } | null>(null);
  const [confirmKey, setConfirmKey] = useState('');
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [result, setResult] = useState<ErasureResult | null>(null);

  // the erasure log's "Inventory" opens this page for one person
  useEffect(() => {
    const subject = params.get('subject');
    if (subject) {
      setKind('subject');
      setValue(subject);
      setLookup({ kind: 'subject', value: subject });
    }
  }, [params]);

  const { data, isFetching, isError } = useCustom<{
    matches: PersonInfo[];
    inventory: Inventory | null;
  }>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: !!lookup },
    meta: {
      operation: 'erasureInventory',
      fields: [
        { matches: PERSON },
        {
          inventory: [
            { person: PERSON },
            'mergedIn',
            { deletes: COUNT },
            'cohort',
            'pilot',
            { redactions: COUNT },
            { keptRows: COUNT },
            { log: LOG },
            {
              external: [
                'bucket',
                'account',
                'accessDoc',
                'onLists',
                { files: COUNT },
                'urlFiles',
              ],
            },
            { targets: ['name', 'policy', 'detail'] },
            'byHand',
            'affectedPractices',
            'blockers',
          ],
        },
      ],
      variables: {
        email: {
          name: 'email',
          type: 'String',
          value: lookup?.kind === 'email' ? lookup.value : null,
        },
        subject: {
          name: 'subject',
          type: 'String',
          value: lookup?.kind === 'subject' ? lookup.value : null,
        },
        uid: {
          name: 'uid',
          type: 'String',
          value: lookup?.kind === 'uid' ? lookup.value : null,
        },
      },
    },
  });
  const answer = lookup ? data?.data : undefined;
  const inventory = answer?.inventory ?? null;
  const { mutateAsync, isLoading: erasing } =
    useCustomMutation<ErasureResult>();

  const look = (k: LookupKind, v: string) => {
    if (!v.trim()) return;
    setResult(null);
    setFailure(null);
    setConfirmKey('');
    setReason('');
    setLookup({ kind: k, value: v.trim() });
  };

  const keyMatches = !!inventory && confirmKey.trim() === inventory.person.key;
  const canErase =
    !!inventory &&
    !inventory.blockers.length &&
    keyMatches &&
    !!reason.trim() &&
    !erasing;

  const erase = async () => {
    if (!inventory || !canErase) return;
    setFailure(null);
    try {
      const res = await mutateAsync({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: {
          operation: 'eraseUserData',
          fields: [
            'subject',
            'alreadyErased',
            { steps: STEP },
            { log: LOG },
            'byHand',
            'affectedPractices',
          ],
          variables: {
            subject: {
              name: 'subject',
              type: 'String!',
              value: inventory.person.subject,
            },
            confirm: {
              name: 'confirm',
              type: 'String!',
              value: confirmKey.trim(),
            },
            reason: { name: 'reason', type: 'String!', value: reason.trim() },
          },
        },
      });
      setResult(res.data);
      setLookup(null);
    } catch (error) {
      setFailure(
        graphqlErrorInfo(error).message ||
          translate('backoffice.erasure.failed'),
      );
    }
  };

  const yesNo = (b: boolean) =>
    translate(b ? 'backoffice.erasure.yes' : 'backoffice.erasure.no');

  if (result) {
    // antd's Result keeps only its named props, so the test id rides on a wrapper
    return (
      <div data-testid="erasure-result">
        <Result
          status="success"
          title={`${translate('backoffice.erasure.doneTitle')} — ${
            result.subject
          }`}
          subTitle={
            result.alreadyErased
              ? translate('backoffice.erasure.alreadyErased')
              : undefined
          }
          extra={
            <Link to="/backoffice/erasure/log">
              {translate('backoffice.menu.erasureLog')}
            </Link>
          }
        >
          <Space
            direction="vertical"
            size="large"
            style={{ width: '100%', textAlign: 'left' }}
          >
            <Card size="small" title={translate('backoffice.erasure.steps')}>
              <Table<Step>
                scroll={{ x: 'max-content' }}
                size="small"
                rowKey="store"
                pagination={false}
                dataSource={result.steps}
                data-testid="erasure-result-steps"
                columns={[
                  {
                    title: translate('backoffice.erasure.store'),
                    dataIndex: 'store',
                  },
                  {
                    title: translate('backoffice.erasure.outcome'),
                    dataIndex: 'outcome',
                    render: (v: string) => <Tag>{v}</Tag>,
                  },
                  {
                    title: translate('backoffice.erasure.counts'),
                    dataIndex: 'counts',
                    render: (v: string) => <code>{v}</code>,
                  },
                  {
                    title: translate('backoffice.erasure.note'),
                    dataIndex: 'note',
                  },
                ]}
              />
            </Card>
            {result.byHand.length ? (
              <Alert
                type="warning"
                showIcon
                message={translate('backoffice.erasure.byHand')}
                description={
                  <List
                    size="small"
                    dataSource={result.byHand}
                    renderItem={(l) => <List.Item>{l}</List.Item>}
                  />
                }
              />
            ) : null}
            <Card
              size="small"
              title={translate('backoffice.erasure.toRequester')}
            >
              <Typography.Paragraph copyable>
                {translate('backoffice.erasure.requesterText')}
              </Typography.Paragraph>
            </Card>
            {result.affectedPractices.length ? (
              <Card
                size="small"
                title={`${translate(
                  'backoffice.erasure.toPractice',
                )}: ${result.affectedPractices.join(', ')}`}
              >
                <Typography.Paragraph copyable>
                  {translate('backoffice.erasure.practiceText')}
                </Typography.Paragraph>
              </Card>
            ) : null}
          </Space>
        </Result>
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3}>
          {translate('backoffice.erasure.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          {translate('backoffice.erasure.intro')}
        </Typography.Paragraph>
      </div>

      <Space wrap>
        <Segmented
          value={kind}
          onChange={(v) => setKind(v as LookupKind)}
          options={[
            {
              value: 'email',
              label: translate('backoffice.erasure.lookupEmail'),
            },
            {
              value: 'subject',
              label: translate('backoffice.erasure.lookupKey'),
            },
            { value: 'uid', label: translate('backoffice.erasure.lookupUid') },
          ]}
          data-testid="erasure-lookup-kind"
        />
        <Input
          style={{ width: 360 }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={() => look(kind, value)}
          data-testid="erasure-lookup-value"
        />
        <Button
          type="primary"
          onClick={() => look(kind, value)}
          loading={!!lookup && isFetching}
          data-testid="erasure-lookup-button"
        >
          {translate('backoffice.erasure.inventory')}
        </Button>
      </Space>

      {isError ? (
        <Alert
          type="error"
          showIcon
          message={translate('backoffice.erasure.failed')}
        />
      ) : null}
      {answer && !answer.matches.length ? (
        <Alert
          type="info"
          showIcon
          message={translate('backoffice.erasure.notFound')}
          data-testid="erasure-not-found"
        />
      ) : null}
      {answer && answer.matches.length > 1 ? (
        <Card size="small" title={translate('backoffice.erasure.several')}>
          <List
            dataSource={answer.matches}
            renderItem={(p) => (
              <List.Item
                actions={[
                  <Button
                    key="pick"
                    size="small"
                    onClick={() => look('subject', p.subject)}
                  >
                    {translate('backoffice.erasure.inventory')}
                  </Button>,
                ]}
              >
                <code>{p.subject}</code> {p.email}
              </List.Item>
            )}
          />
        </Card>
      ) : null}

      {inventory ? (
        <Space
          direction="vertical"
          size="middle"
          style={{ width: '100%' }}
          data-testid="erasure-inventory"
        >
          <Descriptions
            bordered
            size="small"
            column={2}
            title={translate('backoffice.erasure.person')}
          >
            <Descriptions.Item label="subject">
              <code data-testid="erasure-inventory-subject">
                {inventory.person.subject}
              </code>
            </Descriptions.Item>
            <Descriptions.Item label="e-mail">
              {inventory.person.email ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="uid">
              {inventory.person.uid ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('backoffice.erasure.role')}>
              {inventory.person.role ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('backoffice.erasure.practice')}>
              {inventory.person.practiceId ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item
              label={translate('backoffice.erasure.stripeCustomer')}
            >
              {yesNo(inventory.person.stripeCustomer)}
            </Descriptions.Item>
            {inventory.mergedIn.length ? (
              <Descriptions.Item
                label={translate('backoffice.erasure.mergedIn')}
                span={2}
              >
                {inventory.mergedIn.join(', ')}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          {inventory.blockers.length ? (
            <Alert
              type="error"
              showIcon
              message={translate('backoffice.erasure.blockers')}
              description={
                <List
                  size="small"
                  dataSource={inventory.blockers}
                  renderItem={(b) => <List.Item>{b}</List.Item>}
                />
              }
              data-testid="erasure-blockers"
            />
          ) : null}

          <Space align="start" wrap size="middle" style={{ width: '100%' }}>
            <Card
              size="small"
              title={translate('backoffice.erasure.deletes')}
              style={{ minWidth: 300 }}
            >
              {countsTable(
                inventory.deletes,
                translate('backoffice.erasure.none'),
                'erasure-inventory-deletes',
              )}
              <Space style={{ marginTop: 8 }}>
                {inventory.cohort ? <Tag>es_cohort</Tag> : null}
                {inventory.pilot ? <Tag>appConfig/es.pilotSubjects</Tag> : null}
              </Space>
            </Card>
            <Card
              size="small"
              title={translate('backoffice.erasure.redactions')}
              style={{ minWidth: 300 }}
            >
              {countsTable(
                inventory.redactions,
                translate('backoffice.erasure.none'),
                'erasure-inventory-redactions',
              )}
            </Card>
            <Card
              size="small"
              title={translate('backoffice.erasure.keptRows')}
              style={{ minWidth: 300 }}
            >
              {countsTable(
                inventory.keptRows,
                translate('backoffice.erasure.none'),
                'erasure-inventory-kept',
              )}
            </Card>
          </Space>

          <Card size="small" title={translate('backoffice.erasure.log')}>
            <Typography.Text data-testid="erasure-inventory-log">
              {translate('backoffice.erasure.logLine', inventory.log)}
            </Typography.Text>
          </Card>

          <Card
            size="small"
            title={`${translate('backoffice.erasure.outside')} (${
              inventory.external.bucket
            })`}
          >
            <Descriptions size="small" column={2}>
              <Descriptions.Item
                label={translate('backoffice.erasure.account')}
              >
                {yesNo(inventory.external.account)}
              </Descriptions.Item>
              <Descriptions.Item
                label={translate('backoffice.erasure.accessDoc')}
              >
                {yesNo(inventory.external.accessDoc)}
              </Descriptions.Item>
              <Descriptions.Item
                label={translate('backoffice.erasure.onLists')}
              >
                {inventory.external.onLists}
              </Descriptions.Item>
              <Descriptions.Item
                label={translate('backoffice.erasure.urlFiles')}
              >
                {inventory.external.urlFiles}
              </Descriptions.Item>
            </Descriptions>
            {countsTable(
              inventory.external.files,
              translate('backoffice.erasure.none'),
              'erasure-inventory-files',
            )}
          </Card>

          <Card size="small" title={translate('backoffice.erasure.targets')}>
            <Table
              scroll={{ x: 'max-content' }}
              size="small"
              rowKey="name"
              pagination={false}
              dataSource={inventory.targets}
              showHeader={false}
              columns={[
                { dataIndex: 'name', render: (v: string) => <code>{v}</code> },
                {
                  dataIndex: 'policy',
                  render: (v: string) => <Tag color={POLICY_COLOR[v]}>{v}</Tag>,
                },
                { dataIndex: 'detail' },
              ]}
            />
          </Card>

          {inventory.byHand.length ? (
            <Alert
              type="warning"
              showIcon
              message={translate('backoffice.erasure.byHand')}
              description={
                <List
                  size="small"
                  dataSource={inventory.byHand}
                  renderItem={(l) => <List.Item>{l}</List.Item>}
                />
              }
            />
          ) : null}

          <Typography.Text>
            {translate('backoffice.erasure.practices')}:{' '}
            {inventory.affectedPractices.join(', ') ||
              translate('backoffice.erasure.none')}
          </Typography.Text>

          <Card
            title={translate('backoffice.erasure.confirmTitle')}
            style={{ borderColor: token.colorError }}
            data-testid="erasure-confirm-section"
          >
            <Space
              direction="vertical"
              size="middle"
              style={{ width: '100%', maxWidth: 560 }}
            >
              <Typography.Paragraph>
                {translate('backoffice.erasure.confirmHint', {
                  key: inventory.person.key,
                })}
              </Typography.Paragraph>
              <Input
                value={confirmKey}
                onChange={(e) => setConfirmKey(e.target.value)}
                placeholder={translate('backoffice.erasure.confirmKey')}
                status={confirmKey && !keyMatches ? 'error' : undefined}
                data-testid="erasure-confirm-key"
              />
              {confirmKey && !keyMatches ? (
                <Typography.Text
                  type="danger"
                  data-testid="erasure-confirm-mismatch"
                >
                  {translate('backoffice.erasure.confirmMismatch')}
                </Typography.Text>
              ) : null}
              <Input.TextArea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={translate('backoffice.erasure.reasonPlaceholder')}
                autoSize={{ minRows: 2, maxRows: 4 }}
                data-testid="erasure-reason"
              />
              {failure ? (
                <Alert
                  type="error"
                  showIcon
                  message={failure}
                  data-testid="erasure-failure"
                />
              ) : null}
              <Button
                type="primary"
                danger
                disabled={!canErase}
                loading={erasing}
                onClick={erase}
                data-testid="erasure-erase-button"
              >
                {erasing
                  ? translate('backoffice.erasure.erasing')
                  : translate('backoffice.erasure.confirm')}
              </Button>
            </Space>
          </Card>
        </Space>
      ) : null}
    </Space>
  );
};
